import { useState, useEffect, useCallback } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, CandlestickChart, Brain, LineChart, Wallet, TrendingUp,
  History, Terminal, LogOut, Menu, Sun, Moon, OctagonX, ChevronRight,
  Wifi, WifiOff, AlertTriangle, RefreshCw, ExternalLink, Play, Settings,
  HeartPulse,
} from "lucide-react";
import api from "@/lib/api";
import PortalSwitch from "@/components/PortalSwitch";
import NotificationBell from "@/components/NotificationBell";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { LogoMark } from "@/components/Logo";
import InfoTip from "./InfoTip";

const NAV = [
  { to: "/ops", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/ops/trading", label: "Live Trading", icon: CandlestickChart },
  { to: "/ops/brain", label: "MentorAI Brain", icon: Brain },
  { to: "/ops/charts", label: "Charts", icon: LineChart },
  { to: "/ops/accounts", label: "Accounts", icon: Wallet },
  { to: "/ops/performance", label: "Performance", icon: TrendingUp },
  { to: "/ops/history", label: "Trade History", icon: History },
  { to: "/ops/logs", label: "System / Logs", icon: Terminal },
  { to: "/ops/settings", label: "Settings", icon: Settings },
];

const OpsNavItems = ({ onNavigate, collapsed }) => (
  <nav className="flex-1 px-2 space-y-0.5">
    {NAV.map((n) => (
      <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate}
        data-testid={`ops-nav-${n.label.toLowerCase().replace(/[\s/]+/g, "-")}`}
        className={({ isActive }) =>
          `flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] transition-colors duration-150 ${
            isActive
              ? "bg-[hsl(var(--ops-surface-2))] text-[hsl(var(--ops-text))] font-medium"
              : "text-[hsl(var(--ops-text-muted))] hover:bg-[hsl(var(--ops-surface))] hover:text-[hsl(var(--ops-text))]"
          }`
        }>
        <n.icon className="size-4 shrink-0" />
        {!collapsed && <span>{n.label}</span>}
      </NavLink>
    ))}
  </nav>
);

export default function OpsLayout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [alertCounts, setAlertCounts] = useState({ unacknowledged: 0, critical: 0 });
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [tick, setTick] = useState(0);
  const [resumeOpen, setResumeOpen] = useState(false);
  const [resumeText, setResumeText] = useState("");
  const initials = (user?.name || "A").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const fetchStatus = useCallback(async () => {
    try {
      const [statusRes, alertRes] = await Promise.all([
        api.get("/ops/status"),
        api.get("/ops/alerts?acknowledged=false&limit=1"),
      ]);
      setStatus(statusRes.data);
      setAlertCounts({ unacknowledged: alertRes.data.unacknowledged, critical: alertRes.data.critical });
      setLastUpdated(new Date());
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchStatus();
    if (!autoRefresh) return;
    const iv = setInterval(fetchStatus, 3000);
    return () => clearInterval(iv);
  }, [fetchStatus, autoRefresh]);

  const doKillSwitch = async () => {
    try {
      await api.post("/ops/kill-switch", {});
      toast.error("EMERGENCY STOP sent: the bot will close all open trades, cancel all orders and stop", { duration: 10000 });
      fetchStatus();
    } catch (e) {
      toast.error("Failed to send the emergency stop. Try again, or stop the bot on the PC.");
    }
  };

  const doResume = async () => {
    try {
      await api.post("/ops/resume", { confirm: "RESUME" });
      toast.success("Emergency stop lifted. Trading is allowed again.");
      setResumeOpen(false);
      setResumeText("");
      fetchStatus();
    } catch (e) {
      toast.error("Failed to lift the emergency stop");
    }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  const isHalted = status?.halted === true || status?.bot_status === "halted";
  const ack = status?.command_ack && status?.command && status.command_ack.command_id === status.command.command_id ? status.command_ack : null;
  const isRunning = status?.bot_status === "running";
  const isError = status?.bot_status === "error";
  const ntConnected = status?.ninjatrader_connected;
  const mdConnected = status?.market_data_connected;
  const env = status?.environment || "SIM";

  // Heartbeat freshness (30s threshold — generous for 5s poll + 3s heartbeat interval)
  const lastHb = status?.last_heartbeat;
  const hbAge = lastHb ? Math.round((Date.now() - new Date(lastHb).getTime()) / 1000) : null;
  const hbFresh = hbAge !== null && hbAge < 30;
  const botTip = isHalted ? "Bot status: stopped by the emergency stop. It will not trade until an admin resumes it."
    : isRunning ? "Bot status: running and allowed to trade."
    : isError ? "Bot status: the bot reported a problem. Check System / Logs."
    : status?.bot_status ? `Bot status: ${status.bot_status}. The bot is not trading right now.`
    : "Bot status: no report from the bot yet.";

  return (
    <TooltipProvider delayDuration={200}>
      <div data-testid="ops-layout" className="min-h-screen ops-bg flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex w-[220px] shrink-0 flex-col h-dvh sticky top-0 bg-[hsl(var(--ops-bg))] border-r border-[hsl(var(--ops-border))]">
          <div className="h-14 flex items-center gap-2 px-4 border-b border-[hsl(var(--ops-border))]">
            <LogoMark className="size-8 ring-white/15" />
            <div>
              <div className="text-sm font-display font-semibold text-[hsl(var(--ops-text))] leading-none">MentorAI</div>
              <div className="text-[10px] text-[hsl(var(--ops-text-muted))] tracking-wider">OPS CENTER</div>
            </div>
          </div>
          <div className="flex-1 py-3 overflow-y-auto"><OpsNavItems /></div>
          <div className="px-2 pb-2">
            <NavLink to="/admin" data-testid="ops-goto-admin" className="flex items-center gap-2 rounded-lg px-3 py-2 text-[11px] text-[hsl(var(--ops-text-muted))] hover:text-[hsl(var(--ops-text))] hover:bg-[hsl(var(--ops-surface))] transition-colors duration-150">
              <ExternalLink className="size-3.5" /> Admin Panel
            </NavLink>
          </div>
          <div className="p-3 border-t border-[hsl(var(--ops-border))]">
            <div className="flex items-center gap-2 px-1">
              <Avatar className="size-7"><AvatarImage src={user?.picture} /><AvatarFallback className="bg-[hsl(var(--ops-brand))]/20 text-[hsl(var(--ops-brand))] text-[10px]">{initials}</AvatarFallback></Avatar>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-[hsl(var(--ops-text))] truncate">{user?.name}</div>
                <div className="text-[10px] text-[hsl(var(--ops-text-muted))] truncate">{user?.email}</div>
              </div>
            </div>
          </div>
        </aside>

        <div className="flex-1 min-w-0 flex flex-col">
          {/* Emergency-stop banner */}
          {isHalted && (
            <div data-testid="ops-halted-banner" className="bg-[hsl(var(--ops-critical))] text-white px-4 py-2 text-center text-sm font-semibold flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
              <OctagonX className="size-4" />
              EMERGENCY STOP ACTIVE: bot told to close all trades, cancel orders and stop
              <span data-testid="ops-halted-ack" className="font-normal text-white/85 text-xs">
                {ack?.flat
                  ? `Bot confirmed flat${ack.flat_at ? ` at ${new Date(ack.flat_at).toLocaleTimeString()}` : ""}`
                  : ack
                    ? `Bot acknowledged, still closing${ack.open_positions != null ? ` (${ack.open_positions} open)` : ""}`
                    : hbFresh
                      ? "Waiting for the bot to confirm"
                      : "Bot offline: it gets the stop on its next heartbeat"}
              </span>
              <Button data-testid="ops-resume-button" size="sm" variant="outline" className="ml-2 max-sm:ml-0 h-7 border-white/30 bg-transparent text-white hover:bg-white/10" onClick={() => setResumeOpen(true)}>
                <Play className="size-3 mr-1" /> Resume
              </Button>
            </div>
          )}

          {/* Resume needs an explicit, typed confirmation */}
          <AlertDialog open={resumeOpen} onOpenChange={(o) => { setResumeOpen(o); if (!o) setResumeText(""); }}>
            <AlertDialogContent data-testid="ops-resume-dialog" className="bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text))] max-sm:w-[calc(100%-1.5rem)] max-sm:rounded-lg max-sm:p-5 max-sm:max-h-[90dvh] max-sm:overflow-y-auto">
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <Play className="size-5 text-[hsl(var(--ops-long))]" /> Lift the emergency stop?
                </AlertDialogTitle>
                <AlertDialogDescription className="text-[hsl(var(--ops-text-muted))]">
                  This tells the bot trading is allowed again. {ack?.flat ? "The bot confirmed it is flat." : "The bot has NOT confirmed it is flat yet."} Type RESUME to confirm.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <Input data-testid="ops-resume-input" value={resumeText} onChange={(e) => setResumeText(e.target.value)} placeholder="RESUME"
                className="bg-[hsl(var(--ops-surface-2))] border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text))]" />
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="ops-resume-cancel-button" className="bg-[hsl(var(--ops-surface-2))] border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text))] hover:bg-[hsl(var(--ops-surface))]">Cancel</AlertDialogCancel>
                <Button data-testid="ops-resume-confirm-button" disabled={resumeText.trim().toUpperCase() !== "RESUME"} onClick={doResume}
                  className="bg-[hsl(var(--ops-long))] hover:bg-[hsl(var(--ops-long))]/90 text-white">
                  <Play className="size-4 mr-1" /> Resume trading
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Top command bar */}
          <header className="h-12 border-b border-[hsl(var(--ops-border))] bg-[hsl(var(--ops-bg))] sticky top-0 z-40 flex items-center justify-between px-3 sm:px-4 max-sm:gap-2 max-sm:px-2">
            <div className="flex items-center gap-2 max-sm:gap-1 max-sm:min-w-0">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button data-testid="ops-mobile-menu" variant="ghost" size="icon" className="lg:hidden size-8 text-[hsl(var(--ops-text-muted))]">
                    <Menu className="size-4" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="p-0 w-[220px] bg-[hsl(var(--ops-bg))] border-r border-[hsl(var(--ops-border))]">
                  <div className="h-14 flex items-center gap-2 px-4 border-b border-[hsl(var(--ops-border))]">
                    <LogoMark className="size-7 ring-white/15" />
                    <span className="text-sm font-display font-semibold text-[hsl(var(--ops-text))]">MentorAI Ops</span>
                  </div>
                  <div className="py-3"><OpsNavItems onNavigate={() => setMobileOpen(false)} /></div>
                </SheetContent>
              </Sheet>

              <PortalSwitch variant="dark" className="hidden sm:inline-flex" />

              {/* Status pills */}
              <div className="flex items-center gap-1.5 max-sm:gap-1 max-sm:min-w-0">
                <Badge data-testid="ops-env-badge" className={`hidden sm:inline-flex rounded-md text-[10px] tracking-wider font-semibold px-2 py-0.5 border-0 ${env === 'LIVE' ? 'bg-[hsl(var(--ops-critical))]/15 text-[hsl(var(--ops-critical))]' : 'bg-[hsl(var(--ops-brand))]/15 text-[hsl(var(--ops-brand))]'}`}>
                  {env}
                </Badge>
                <InfoTip tip={botTip}>
                  <Badge data-testid="ops-status-bot" tabIndex={0} className={`rounded-md text-[10px] px-2 py-0.5 border-0 cursor-help max-sm:px-1.5 max-sm:min-w-0 max-sm:max-w-[96px] ${
                    isRunning ? 'bg-[hsl(var(--ops-long))]/15 text-[hsl(var(--ops-long))]' :
                    isHalted ? 'bg-[hsl(var(--ops-critical))]/15 text-[hsl(var(--ops-critical))]' :
                    isError ? 'bg-[hsl(var(--ops-critical))]/15 text-[hsl(var(--ops-critical))]' :
                    'bg-[hsl(var(--ops-text-muted))]/15 text-[hsl(var(--ops-text-muted))]'
                  }`}>
                    <span className="size-1.5 rounded-full mr-1 inline-block max-sm:shrink-0" style={{ backgroundColor: isRunning ? 'hsl(var(--ops-long))' : isHalted || isError ? 'hsl(var(--ops-critical))' : 'hsl(var(--ops-text-muted))' }} />
                    <span className="max-sm:truncate">{status?.bot_status?.toUpperCase() || "OFFLINE"}</span>
                  </Badge>
                </InfoTip>
                <InfoTip tip={`Broker: the link between the bot and NinjaTrader, the program that places the trades. ${ntConnected ? 'Connected.' : 'Not connected: the bot cannot place or manage trades.'}`}>
                  <span data-testid="ops-status-ninjatrader" tabIndex={0} className={`hidden sm:flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded cursor-help ${ntConnected ? 'text-[hsl(var(--ops-long))]' : 'text-[hsl(var(--ops-critical))]'}`}>
                    {ntConnected ? <Wifi className="size-3" /> : <WifiOff className="size-3" />}
                    <span className="hidden sm:inline">Broker</span>
                  </span>
                </InfoTip>
                <InfoTip tip={`Prices: the live market price feed the bot reads. ${mdConnected ? 'Receiving prices.' : 'No prices coming in: the bot cannot see the market.'}`}>
                  <span data-testid="ops-status-market" tabIndex={0} className={`hidden sm:flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded cursor-help ${mdConnected ? 'text-[hsl(var(--ops-long))]' : 'text-[hsl(var(--ops-critical))]'}`}>
                    {mdConnected ? <Wifi className="size-3" /> : <WifiOff className="size-3" />}
                    <span className="hidden sm:inline">Prices</span>
                  </span>
                </InfoTip>
                <InfoTip tip={`Heartbeat: the bot checks in with this dashboard every few seconds. ${hbFresh ? `Last check-in ${hbAge}s ago. All good.` : hbAge !== null ? `Last check-in ${hbAge}s ago. That is late: the bot or its internet may be down.` : 'No check-in received yet.'}`}>
                  <span data-testid="ops-status-heartbeat" tabIndex={0} className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded cursor-help ${
                    hbFresh ? 'text-[hsl(var(--ops-long))]' : hbAge !== null ? 'text-[hsl(var(--ops-critical))]' : 'text-[hsl(var(--ops-text-muted))]'
                  }`}>
                    <HeartPulse className={`size-3 ${hbFresh ? 'animate-pulse' : ''}`} />
                    <span className="hidden sm:inline">Heartbeat</span>
                  </span>
                </InfoTip>
              </div>
            </div>

            <div className="flex items-center gap-1 max-sm:gap-0.5 max-sm:shrink-0">
              {lastUpdated && (
                <span data-testid="ops-status-last-updated" className="hidden sm:block text-[10px] text-[hsl(var(--ops-text-muted))] mr-2">
                  {lastUpdated.toLocaleTimeString()}
                </span>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button data-testid="ops-auto-refresh-toggle" onClick={() => setAutoRefresh(!autoRefresh)}
                    className={`hidden sm:flex size-7 rounded-md flex items-center justify-center transition-colors duration-150 ${autoRefresh ? 'text-[hsl(var(--ops-brand))] bg-[hsl(var(--ops-brand))]/10' : 'text-[hsl(var(--ops-text-muted))] hover:bg-[hsl(var(--ops-surface))]'}`}>
                    <RefreshCw className={`size-3.5 ${autoRefresh ? 'animate-spin' : ''}`} style={autoRefresh ? { animationDuration: '3s' } : {}} />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Auto-refresh {autoRefresh ? 'ON' : 'OFF'}</TooltipContent>
              </Tooltip>

              {/* Notifications: client messages + system alerts */}
              <NotificationBell variant="ops" alerts={alertCounts.unacknowledged} />

              <button data-testid="ops-theme-toggle" onClick={toggle} className="size-7 rounded-md flex items-center justify-center text-[hsl(var(--ops-text-muted))] hover:bg-[hsl(var(--ops-surface))] transition-colors duration-150">
                {theme === "dark" ? <Sun className="size-3.5" /> : <Moon className="size-3.5" />}
              </button>

              <Separator orientation="vertical" className="h-5 mx-1 max-sm:mx-0.5 bg-[hsl(var(--ops-border))]" />

              {/* Kill Switch */}
              {isHalted ? (
                <Button data-testid="ops-resume-topbar" size="sm" className="h-7 px-3 max-sm:px-2 text-[11px] font-semibold rounded-md bg-[hsl(var(--ops-long))] hover:bg-[hsl(var(--ops-long))]/90 text-white" onClick={() => setResumeOpen(true)}>
                  <Play className="size-3 mr-1" /> RESUME
                </Button>
              ) : (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button data-testid="ops-kill-switch-button" size="sm" variant="destructive" className="h-7 px-3 max-sm:px-2 text-[11px] font-semibold rounded-md bg-[hsl(var(--ops-critical))] hover:bg-[hsl(var(--ops-critical))]/90" aria-label="Emergency stop: close all trades and stop the bot">
                      <OctagonX className="size-3 mr-1" /> <span className="hidden sm:inline">EMERGENCY STOP</span><span className="sm:hidden">STOP</span>
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent data-testid="ops-kill-switch-dialog" className="bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text))] max-sm:w-[calc(100%-1.5rem)] max-sm:rounded-lg max-sm:p-5 max-sm:max-h-[90dvh] max-sm:overflow-y-auto">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="flex items-center gap-2 text-[hsl(var(--ops-critical))]">
                        <OctagonX className="size-5" /> Emergency stop: shut the whole bot down?
                      </AlertDialogTitle>
                      <AlertDialogDescription asChild>
                        <div className="text-[hsl(var(--ops-text-muted))] text-sm space-y-2">
                          <p>This <strong className="text-[hsl(var(--ops-text))]">closes ALL open trades</strong> at market, cancels every pending order, and stops the bot. No new trades will be placed.</p>
                          <p>The bot stays stopped until an admin resumes it here.</p>
                        </div>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="ops-kill-switch-cancel-button" className="bg-[hsl(var(--ops-surface-2))] border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text))] hover:bg-[hsl(var(--ops-surface))]">Cancel</AlertDialogCancel>
                      <AlertDialogAction data-testid="ops-kill-switch-confirm-button" onClick={doKillSwitch} className="bg-[hsl(var(--ops-critical))] text-white hover:bg-[hsl(var(--ops-critical))]/90">
                        <OctagonX className="size-4 mr-1" /> Close all trades &amp; stop
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="ops-user-menu" className="flex items-center gap-1 rounded-md pr-1 hover:bg-[hsl(var(--ops-surface))] transition-colors duration-150 p-0.5 ml-1 max-sm:ml-0.5 max-sm:pr-0.5">
                    <Avatar className="size-6"><AvatarImage src={user?.picture} /><AvatarFallback className="bg-[hsl(var(--ops-brand))]/20 text-[hsl(var(--ops-brand))] text-[9px]">{initials}</AvatarFallback></Avatar>
                    <ChevronRight className="size-3 text-[hsl(var(--ops-text-muted))] rotate-90 max-sm:hidden" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 bg-[hsl(var(--ops-surface))] border-[hsl(var(--ops-border))] text-[hsl(var(--ops-text))]">
                  <DropdownMenuLabel>
                    <div className="font-medium text-sm">{user?.name}</div>
                    <div className="text-[11px] text-[hsl(var(--ops-text-muted))] font-normal">{user?.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-[hsl(var(--ops-border))]" />
                  <DropdownMenuItem onClick={doLogout} className="text-[hsl(var(--ops-critical))] focus:text-[hsl(var(--ops-critical))] focus:bg-[hsl(var(--ops-surface-2))]">
                    <LogOut className="size-4 mr-2" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          {/* Phone: the Admin | Ops switch gets its own row so the STOP button always fits */}
          <div className="sm:hidden sticky top-12 z-30 flex justify-center px-3 py-2 border-b border-[hsl(var(--ops-border))] bg-[hsl(var(--ops-bg))]">
            <PortalSwitch variant="dark" />
          </div>

          <main className="flex-1 w-full px-3 sm:px-4 lg:px-6 py-4 sm:py-5">
            <Outlet context={{ status, alertCounts, autoRefresh, fetchStatus }} />
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}
